import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiMail, FiPhone, FiSend, FiArrowUp } = FiIcons;

const AsteriskIcon = () => (
  <svg 
    className="w-8 h-8 md:w-16 md:h-16 text-teal-400 mx-4 md:mx-8 animate-spin-slow" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
  >
    <path d="M12 2v20M2 12h20M4.929 4.929l14.142 14.142M4.929 19.071L19.071 4.929" />
  </svg>
);

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const marqueeText = [
    "RECOVERY", "MOBILITY", "STRENGTH", "BALANCE", "WELLNESS"
  ];

  return (
    <footer className="bg-gradient-to-r from-[#020d2b] to-[#071946] pt-16 lg:pt-24 relative overflow-hidden font-sans rounded-tl-[50px] lg:rounded-tl-[100px]">
      <div className="absolute top-0 right-0 w-[300px] lg:w-[600px] h-[300px] lg:h-[600px] bg-teal-600/5 rounded-full blur-[80px] lg:blur-[120px] pointer-events-none"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-20 mb-12 lg:mb-24">
          
          {/* Left Column - White Card */}
          <div className="lg:w-[350px] flex-shrink-0">
            <div className="bg-white rounded-2xl p-6 lg:p-8 text-slate-900 h-full shadow-2xl relative z-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-teal-600 rounded-lg flex items-center justify-center text-white font-bold text-lg lg:text-xl shadow-lg">
                  <span className="text-base lg:text-lg">DIP</span>
                </div>
                <span className="text-xl lg:text-2xl font-bold text-slate-900 tracking-tight">DIP Physio</span>
              </div>
              <p className="text-slate-600 text-sm mb-8 leading-relaxed font-medium">
                Dedicated to providing flexible & accessible physiotherapy services in Surat.
              </p>
              
              <div className="space-y-4 text-sm font-semibold">
                <div className="flex justify-between border-b border-slate-100 pb-3">
                  <span className="text-slate-500">Mon - Sat</span>
                  <span className="text-slate-900 text-right">
                    9:00 AM - 1:00 PM<br/>
                    4:00 PM - 8:30 PM
                  </span>
                </div>
                <div className="flex justify-between border-b border-slate-100 pb-3">
                  <span className="text-slate-500">Sunday</span>
                  <span className="text-slate-900">Closed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex-grow flex flex-col justify-between">
            {/* Top Row: Links & Newsletter */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 mb-12 lg:mb-16">
              <div>
                <h4 className="text-white text-lg lg:text-xl font-bold mb-4 lg:mb-8">Quick Links</h4>
                <ul className="space-y-3 lg:space-y-4 text-slate-300 text-sm font-medium">
                  <li><a href="#about" className="hover:text-teal-400 transition-colors">About Us</a></li>
                  <li><a href="#services" className="hover:text-teal-400 transition-colors">Services</a></li>
                  <li><a href="#contact" className="hover:text-teal-400 transition-colors">Contact</a></li>
                  <li><a href="#booking" className="hover:text-teal-400 transition-colors">Book Now</a></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-lg lg:text-xl font-bold mb-4 lg:mb-8">Services</h4>
                <ul className="space-y-3 lg:space-y-4 text-slate-300 text-sm font-medium">
                  <li><a href="#" className="hover:text-teal-400 transition-colors">Sports Rehab</a></li>
                  <li><a href="#" className="hover:text-teal-400 transition-colors">Post-Surgical</a></li>
                  <li><a href="#" className="hover:text-teal-400 transition-colors">Chronic Pain</a></li>
                  <li><a href="#" className="hover:text-teal-400 transition-colors">Posture Correction</a></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-lg lg:text-xl font-bold mb-4 lg:mb-8">Newsletter</h4>
                <p className="text-slate-300 text-sm mb-4 lg:mb-6 leading-relaxed">
                  Join the Community and Receive Our Monthly Tips.
                </p>
                <form className="relative" onSubmit={(e) => e.preventDefault()}>
                  <input 
                    type="email" 
                    placeholder="Your Email" 
                    className="w-full bg-white/10 text-white placeholder-slate-400 border border-white/20 rounded-lg pl-4 pr-12 py-3 lg:py-4 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium backdrop-blur-sm"
                  />
                  <button className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 lg:w-10 lg:h-10 bg-teal-600 hover:bg-teal-700 rounded-lg flex items-center justify-center text-white transition-colors">
                    <SafeIcon icon={FiSend} className="w-3 h-3 lg:w-4 lg:h-4 ml-0.5" />
                  </button>
                </form>
              </div>
            </div>

            {/* Bottom Row: Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 border-t border-white/10">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 lg:w-14 lg:h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs mb-1 font-medium uppercase tracking-wide">Visit Our Clinic</p>
                  <p className="text-white font-bold text-sm">108 - B Complex, Surat</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 lg:w-14 lg:h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiMail} className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs mb-1 font-medium uppercase tracking-wide">General Inquiries</p>
                  <p className="text-white font-bold text-xs lg:text-sm truncate max-w-[150px]">dipaksahani229@gmail</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 lg:w-14 lg:h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiPhone} className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs mb-1 font-medium uppercase tracking-wide">Call Us</p>
                  <p className="text-white font-bold text-sm">95109 18372</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee Animation Section */}
      <div className="relative w-full border-t border-white/5 py-8 lg:py-12 overflow-hidden bg-black/10 backdrop-blur-sm">
        <div className="flex whitespace-nowrap animate-marquee items-center">
          {[...Array(6)].map((_, i) => (
            <React.Fragment key={i}>
              {marqueeText.map((text, index) => (
                <div key={`${i}-${index}`} className="flex items-center">
                  <span 
                    className="text-5xl md:text-8xl font-bold text-transparent stroke-text uppercase tracking-wider mx-2 md:mx-4 opacity-40"
                    style={{ WebkitTextStroke: '1px rgba(255, 255, 255, 0.3)' }}
                  >
                    {text}
                  </span>
                  <AsteriskIcon />
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Bottom Copyright Bar */}
      <div className="bg-white rounded-t-[30px] lg:rounded-t-[50px] py-6 relative z-10 -mt-1">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between relative text-center md:text-left">
          <p className="text-slate-600 text-sm font-semibold mb-4 md:mb-0">
            © 2025, <span className="text-teal-600">DIP Physio</span>. All Rights Reserved.
          </p>
          <div className="flex items-center gap-6">
            <div className="flex space-x-4 text-xs lg:text-sm font-bold text-slate-800">
              <a href="#" className="hover:text-teal-600 transition-colors">Privacy</a>
              <span className="text-slate-300">|</span>
              <a href="#" className="hover:text-teal-600 transition-colors">Terms</a>
            </div>
            <button 
              onClick={scrollToTop}
              className="w-10 h-10 lg:w-12 lg:h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center hover:bg-slate-900 transition-colors shadow-lg"
            >
              <SafeIcon icon={FiArrowUp} className="w-4 h-4 lg:w-5 lg:h-5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;